// Shared world-space constants. 1 world unit = 1 LEGO stud pitch.
// Everything in the game snaps to this grid so the voxel look stays coherent.
//
// SCALE NOTE (read before changing TILE)
// -------------------------------------
// A tile is one real LEGO straight track piece: 16 studs long, ~8 studs wide,
// rails 5 studs apart. That single decision fixes everything else:
//
//   * a curve tile is a quarter of a circle of radius TILE/2 = 8 studs;
//   * a car body is rigid, so a car of length L placed tangentially on that arc
//     hangs sqrt(R^2 + L^2/4) - R off the rail centre line at its ends. At
//     R = 8 that is 3.3 studs for a 16-stud loco and 2.5 for a 13-stud wagon —
//     about what a real 6-wide loco does on an R40 curve. A 30-stud loco (what
//     this game shipped with when TILE was 8) hangs 9 studs off the rail, which
//     is why the trains used to sit diagonally across three tracks at once.
//
// So: TILE bounds car length. Car length must stay under ~TILE, and the models
// in src/data/trains are built to that budget. Raising TILE without lengthening
// the trains only makes the stud field finer; lowering it derails everything.

export const TILE = 16; // world units per track tile edge = one LEGO track piece
export const ELEVATION_HEIGHT = 16; // y of the raised (bridge) level = one full tile
export const PLATE = 0.4; // one LEGO plate
export const BRICK = PLATE * 3; // one LEGO brick = 3 plates = 1.2
export const STUD_R = 0.3; // stud radius
export const STUD_H = 0.225; // stud height above the surface
// A real LEGO stud is 1.8 mm tall and 4.8 mm across on an 8 mm pitch, so in
// stud-pitch units it is 0.225 tall by 0.6 across — a height/diameter ratio of
// 0.375. At 0.17 the studs were 25% too short, which cost them their silhouette:
// at a grazing angle they read as flat crescents printed on the plate rather than
// as moulded cylinders, and that mismatch against the properly modelled studs on
// trains and roof plates is what made the ground look textured instead of built.
export const STUD_PITCH = 1; // centre-to-centre stud spacing

export const FIXED_DT = 1 / 120; // simulation timestep, seconds
export const MAX_STEPS_PER_FRAME = 8; // spiral-of-death guard

// Real LEGO track gauge is 37.5 mm on an 8 mm stud pitch, i.e. 4.7 studs, and
// every 6- and 8-wide LEGO train ever made rides on it. It is a property of the
// rolling stock, not of the layout, so it does NOT scale with TILE: 5 studs
// between rail centres inside a ~7.4-stud roadbed on a 16-stud tile is exactly
// the proportion of a real track piece.
export const RAIL_GAUGE = 5; // distance between rail centres
export const RAILHEAD_Y = 0.9; // y of the top of the rail at level 0
export const CURVE_RADIUS = TILE / 2; // quarter-arc radius inside a curve tile

export const PORTS = ['N', 'E', 'S', 'W'];
export const PORT_DIR = {
  N: [0, -1],
  E: [1, 0],
  S: [0, 1],
  W: [-1, 0],
};

export const LEVEL_COLLIDE_EPS = 0.35; // levels closer than this can collide
export const SWITCH_LOCKOUT_DIST = 8; // can't flip a switch closer than this ahead
